import { z, type ZodTypeAny } from 'zod';
import { LogisticOperatorStatus } from '../enums';
import {
  DEFAULT_GPS_TRACKING_VALUES,
  gpsTrackingSchema,
  type LogisticOperatorJsonConfig,
} from './GpsTracking';

export interface LogisticOperatorApi {
  id: string;
  name: string;
  businessName: string;
  taxNumber?: string;
  status: LogisticOperatorStatus;
  addressId: string;
  logisticOperatorGroupId?: string;
  externalId?: string | null;
  headquarter?: boolean;
  parentId?: string | null;
  jsonConfig?: LogisticOperatorJsonConfig | null;
  address?: {
    id: string;
    neighborhoodId: string;
    stateOrDepartmentId?: string;
    cityId?: string;
    streetName: string;
    corner: string;
    doorNumber: string;
    apartmentNumber?: string;
    lat?: string | number | null;
    long?: string | number | null;
    stateOrDepartment?: {
      id: string;
      name: string;
    };
    city?: {
      id: string;
      name: string;
    };
    neighborhood?: {
      id: string;
      name: string;
    };
  };
}

export interface LogisticOperator {
  id: string;
  externalId?: string | null;
  fullName: string;
  businessName: string;
  taxNumber?: string;
  status: LogisticOperatorStatus;
  addressId?: string;
  logisticOperatorGroupId?: string;
  headquarter?: boolean;
  parentId?: string | null;
  jsonConfig?: LogisticOperatorJsonConfig | null;
  address?: {
    id: string;
    neighborhoodId: string;
    stateOrDepartmentId?: string;
    cityId?: string;
    streetName: string;
    corner: string;
    doorNumber: string;
    apartmentNumber?: string;
    lat?: string | number | null;
    long?: string | number | null;
    stateOrDepartment?: {
      id: string;
      name: string;
    };
    city?: {
      id: string;
      name: string;
    };
    neighborhood?: {
      id: string;
      name: string;
    };
  };
}

export interface LogisticOperatorsFilter {
  status?: LogisticOperatorStatus;
  name?: string;
  businessName?: string;
  selectedOperatorIdFromName?: string;
  selectedOperatorIdFromBusiness?: string;
}

export interface NewLogisticOperator {
  name: string;
  businessName: string;
  taxNumber?: string;
  addressId?: string;
  neighborhoodId?: string;
  headquarter: boolean;
  streetName?: string;
  corner?: string;
  doorNumber?: string;
  apartmentNumber?: string;
  status: LogisticOperatorStatus;
  logisticOperatorGroupId?: string;
  parentId?: string | null;
  lat?: string;
  long?: string;
  jsonConfig?: LogisticOperatorJsonConfig | null;
}

export interface UpdateLogisticOperator {
  id: string;
  name?: string;
  businessName?: string;
  taxNumber?: string;
  headquarter?: boolean;
  neighborhoodId?: string;
  streetName?: string;
  corner?: string;
  doorNumber?: string;
  apartmentNumber?: string;
  status?: LogisticOperatorStatus;
  logisticOperatorGroupId?: string;
  parentId?: string | null;
  lat?: string;
  long?: string;
  jsonConfig?: LogisticOperatorJsonConfig | null;
}

const logisticOperatorBaseSchema = z.object({
  name: z
    .string()
    .min(1, 'El nombre es requerido')
    .max(100, 'El nombre no puede exceder 100 caracteres'),
  businessName: z
    .string()
    .min(1, 'La razón social es requerida')
    .max(100, 'La razón social no puede exceder 100 caracteres'),
  taxNumber: z.string().optional(),
  headquarter: z.boolean().default(false),
  status: z.nativeEnum(LogisticOperatorStatus),
  logisticOperatorGroupId: z.string().optional(),
  parentId: z.string().uuid().or(z.literal('')).optional(),
  addressId: z.string().uuid().optional(),
  stateId: z.string().min(1, 'Debe seleccionar un departamento'),
  cityId: z.string().min(1, 'Debe seleccionar una ciudad'),
  neighborhoodId: z.string().optional().or(z.literal('')),
  streetName: z.string().min(1, 'La calle es requerida'),
  corner: z.string().optional(),
  doorNumber: z.string().min(1, 'El número de puerta es requerido'),
  apartmentNumber: z.string().optional(),
  countryId: z.string().optional(),
  gpsTracking: gpsTrackingSchema.default(() => ({
    ...DEFAULT_GPS_TRACKING_VALUES,
  })),
});

const withHeadquarterValidation = <T extends ZodTypeAny>(
  schema: T
): z.ZodEffects<T> =>
  schema.superRefine((data: any, ctx) => {
    if (data.headquarter) {
      if (data.parentId && data.parentId !== '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['parentId'],
          message: 'Una casa matriz no puede ser sucursal.',
        });
      }
      return;
    }

    if (!data.parentId || data.parentId === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['parentId'],
        message: 'Debe seleccionar la casa matriz.',
      });
    }
  });

export const logisticOperatorSchema = withHeadquarterValidation(
  logisticOperatorBaseSchema
);

export const updateLogisticOperatorSchema = withHeadquarterValidation(
  logisticOperatorBaseSchema.partial().extend({
    id: z.string().uuid(),
  })
);

export type LogisticOperatorSchema = z.infer<typeof logisticOperatorSchema>;
export type UpdateLogisticOperatorSchema = z.infer<
  typeof updateLogisticOperatorSchema
>;

export const defaultLogisticOperatorValues: LogisticOperatorSchema = {
  name: '',
  businessName: '',
  taxNumber: '',
  headquarter: false,
  status: LogisticOperatorStatus.CREATED,
  logisticOperatorGroupId: '',
  parentId: '',
  stateId: '',
  cityId: '',
  neighborhoodId: '',
  streetName: '',
  corner: '',
  doorNumber: '',
  apartmentNumber: '',
  gpsTracking: { ...DEFAULT_GPS_TRACKING_VALUES },
};
